/**
 * HOS v4.3 — Browser-simulation IDE sandbox.
 *
 * 3-column IDE layout:
 *   Left   — file tree (mark editable vs read-only)
 *   Center — Monaco editor for selected file (read-only respects file.editable)
 *   Right  — tabs: Diagnostic Console (predefined commands) / Tests (Run Tests button — MOCKED)
 *
 * Single 30-min server-side clock. /sandbox/file persists edits per-keystroke (debounced).
 * /sandbox/run-tests returns deterministic mocked results (TODO_WIRE_REAL_EVAL marked server-side).
 * /sandbox/submit locks the sandbox + marks status=completed.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import Editor from "@monaco-editor/react";
import { Loader2, Send, Clock, FileText, FileCode, FileLock, Terminal, FlaskConical, AlertTriangle, PlayCircle, CheckCircle2, XCircle } from "lucide-react";
import { resolveBackendBase } from "../../lib/api";
import { secondsUntil, fmtClock, normalizeMonacoLanguage } from "../ta/taHelpers";

const API_URL = `${resolveBackendBase()}/api/v2`;

export default function HOSSandboxRunner({ state, onAdvance }) {
    const sandboxDef = (state?.template?.content?.content || []).find(s => s.type === "sandbox_simulation");
    const sb = state?.hos_state?.sandbox || {};
    const files = sandboxDef?.files || [];
    const commands = (sandboxDef?.diagnostic_console || {}).available_commands || [];
    const maxRuns = sandboxDef?.max_test_runs || 10;

    // Local file contents — merge sandbox baseline with any saved edits
    const [fileContents, setFileContents] = useState(() => {
        const init = {};
        for (const f of files) init[f.path] = (sb.file_contents || {})[f.path] ?? f.content;
        return init;
    });
    const [activePath, setActivePath] = useState(files[0]?.path);
    const [savingPath, setSavingPath] = useState(null);
    const saveTimers = useRef({});

    const [rightTab, setRightTab] = useState("tests");
    const [consoleLog, setConsoleLog] = useState(() => sb.console_runs || []);
    const [testRuns, setTestRuns] = useState(() => sb.test_runs || []);
    const [runsRemaining, setRunsRemaining] = useState(() => sb.test_runs_remaining ?? maxRuns);
    const [running, setRunning] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const [secsLeft, setSecsLeft] = useState(() => secondsUntil(sb.deadline_at));
    useEffect(() => {
        const id = setInterval(() => setSecsLeft(secondsUntil(sb.deadline_at)), 1000);
        return () => clearInterval(id);
    }, [sb.deadline_at]);

    const activeFile = files.find(f => f.path === activePath);
    const isEditable = !!activeFile?.editable;
    const lang = normalizeMonacoLanguage(activeFile?.language, "plaintext");

    const onFileEdit = useCallback((value) => {
        if (!isEditable) return;
        setFileContents((m) => ({ ...m, [activePath]: value ?? "" }));
        // Debounced auto-save per file
        clearTimeout(saveTimers.current[activePath]);
        saveTimers.current[activePath] = setTimeout(async () => {
            setSavingPath(activePath);
            try {
                await axios.post(`${API_URL}/assessment/hos/${state.token}/sandbox/file`, {
                    path: activePath, content: value ?? "",
                });
            } catch (_) { /* swallow */ }
            setSavingPath(null);
        }, 1200);
    }, [activePath, isEditable, state.token]);

    const runCommand = async (cmd) => {
        try {
            const r = await axios.post(`${API_URL}/assessment/hos/${state.token}/sandbox/console`, { command: cmd });
            setConsoleLog((l) => [...l, r.data.entry]);
            setRightTab("console");
        } catch (e) { /* swallow */ }
    };

    const runTests = async () => {
        if (running || runsRemaining <= 0) return;
        setRunning(true);
        try {
            const r = await axios.post(`${API_URL}/assessment/hos/${state.token}/sandbox/run-tests`, {});
            setTestRuns((arr) => [...arr, r.data.run]);
            setRunsRemaining(r.data.test_runs_remaining);
        } catch (e) { /* swallow */ }
        setRunning(false);
    };

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await axios.post(`${API_URL}/assessment/hos/${state.token}/sandbox/submit`, {});
            onAdvance && onAdvance();
        } catch (e) {
            setSubmitError(e.response?.data?.detail || "Submit failed.");
            setSubmitting(false);
        }
    };

    const urgent = secsLeft <= (sandboxDef?.timer?.warning_at_seconds || 300);
    const expired = secsLeft <= 0;
    const latest = testRuns[testRuns.length - 1];

    return (
        <div className="h-screen flex flex-col bg-background" data-testid="hos-sandbox">
            {/* Header */}
            <header className="border-b border-border/40 bg-card px-4 py-2 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gray-500">Sandbox · {sandboxDef?.variant}</p>
                        <h1 className="text-sm font-display font-bold text-white">{sandboxDef?.title}</h1>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {savingPath && <span className="text-[11px] text-gray-500 font-mono flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving {savingPath.split("/").pop()}</span>}
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 border ${expired ? "border-red-500/60 text-red-300" : urgent ? "border-amber-500/60 text-amber-200" : "border-border/60 text-gray-300"}`} data-testid="hos-sandbox-clock">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-sm font-mono tabular-nums">{fmtClock(secsLeft)}</span>
                    </div>
                    <button onClick={submit} disabled={submitting}
                        className="px-3 py-1.5 bg-white text-black text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid="hos-sandbox-submit-btn">
                        {submitting ? <><Loader2 className="w-3 h-3 animate-spin" />Submitting…</> : <><Send className="w-3 h-3" />Submit</>}
                    </button>
                </div>
            </header>

            {/* README banner */}
            <div className="border-b border-border/40 bg-[#0a0a0a] px-4 py-2 shrink-0">
                <p className="text-[11px] text-gray-400 leading-relaxed">{sandboxDef?.readme}</p>
            </div>

            {/* Body: 3-column IDE */}
            <div className="flex-1 min-h-0 flex">
                {/* File tree */}
                <aside className="w-60 border-r border-border/40 bg-[#0d0d0d] flex flex-col" data-testid="hos-file-tree">
                    <div className="px-3 py-2 border-b border-border/40">
                        <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500">Files ({files.length})</p>
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {files.map((f) => {
                            const sel = f.path === activePath;
                            const Icon = !f.editable ? FileLock : f.language === "markdown" ? FileText : FileCode;
                            return (
                                <button key={f.path}
                                    onClick={() => setActivePath(f.path)}
                                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs font-mono border-l-2 transition-colors ${sel ? "bg-white/5 border-white text-white" : "border-transparent text-gray-400 hover:bg-white/5 hover:text-gray-200"}`}
                                    data-testid={`hos-file-${f.path.replace(/[^a-z0-9]+/gi, "-")}`}>
                                    <Icon className={`w-3.5 h-3.5 shrink-0 ${!f.editable ? "text-gray-600" : "text-gray-400"}`} />
                                    <span className="truncate">{f.path}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="px-3 py-2 border-t border-border/40 text-[10px] font-mono text-gray-600">
                        <span className="inline-flex items-center gap-1"><FileLock className="w-3 h-3" /> read-only</span>
                    </div>
                </aside>

                {/* Editor */}
                <main className="flex-1 min-w-0 flex flex-col" data-testid="hos-editor-pane">
                    <div className="border-b border-border/40 bg-[#0a0a0a] px-3 py-1.5 flex items-center justify-between shrink-0">
                        <span className="text-[11px] font-mono text-gray-300">{activePath}</span>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider font-mono text-gray-500">{lang}</span>
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${isEditable ? "border-emerald-500/40 text-emerald-300" : "border-gray-600 text-gray-500"}`}>
                                {isEditable ? "editable" : "read-only"}
                            </span>
                        </div>
                    </div>
                    <div className="flex-1 min-h-0">
                        <Editor
                            height="100%"
                            language={lang}
                            value={fileContents[activePath] || ""}
                            theme="vs-dark"
                            onChange={onFileEdit}
                            options={{
                                readOnly: !isEditable,
                                minimap: { enabled: false },
                                fontSize: 13,
                                fontFamily: "JetBrains Mono, ui-monospace, monospace",
                                lineNumbers: "on",
                                automaticLayout: true,
                                wordWrap: "on",
                                scrollBeyondLastLine: false,
                                renderLineHighlight: "none",
                            }}
                        />
                    </div>
                </main>

                {/* Right pane */}
                <aside className="w-[420px] border-l border-border/40 bg-[#0d0d0d] flex flex-col">
                    <div className="border-b border-border/40 flex shrink-0">
                        <TabBtn active={rightTab === "tests"} onClick={() => setRightTab("tests")} icon={<FlaskConical className="w-3.5 h-3.5" />} label={`Tests (${runsRemaining} runs left)`} testid="hos-tab-tests" />
                        <TabBtn active={rightTab === "console"} onClick={() => setRightTab("console")} icon={<Terminal className="w-3.5 h-3.5" />} label={`Console (${consoleLog.length})`} testid="hos-tab-console" />
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {rightTab === "tests" ? (
                            <TestsPane onRun={runTests} running={running} remaining={runsRemaining} latest={latest} runs={testRuns} />
                        ) : (
                            <ConsolePane commands={commands} log={consoleLog} onRun={runCommand} />
                        )}
                    </div>
                </aside>
            </div>

            {expired && (
                <div className="absolute top-16 left-1/2 -translate-x-1/2 border border-red-500/60 bg-red-500/10 px-4 py-2 z-30" data-testid="hos-sandbox-expired">
                    <div className="flex items-center gap-2 text-xs text-red-200"><AlertTriangle className="w-3.5 h-3.5" /> Time is up. Submit to capture your final state.</div>
                </div>
            )}
            {submitError && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 border border-red-500/40 bg-red-500/10 px-4 py-2 z-30">
                    <p className="text-xs text-red-300" data-testid="hos-sandbox-submit-error">{submitError}</p>
                </div>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, icon, label, testid }) {
    return (
        <button onClick={onClick}
            className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center justify-center gap-2 border-b-2 transition-colors ${active ? "border-white text-white bg-white/5" : "border-transparent text-gray-500 hover:text-gray-300"}`}
            data-testid={testid}>
            {icon} {label}
        </button>
    );
}

function TestsPane({ onRun, running, remaining, latest, runs }) {
    return (
        <div className="p-3 space-y-3" data-testid="hos-tests-pane">
            <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/30 px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-200 leading-relaxed">
                    Run Tests is currently <span className="font-semibold">MOCKED</span> with deterministic playback. Real Claude evaluation lands in a future release.
                </p>
            </div>
            <button onClick={onRun} disabled={running || remaining <= 0}
                className="w-full px-3 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="hos-run-tests-btn">
                {running ? <><Loader2 className="w-4 h-4 animate-spin" />Running…</> : <><PlayCircle className="w-4 h-4" />Run Tests</>}
            </button>
            <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500">Runs: {runs.length} · Remaining: {remaining}</p>

            {latest ? (
                <div className="space-y-1.5" data-testid="hos-tests-latest">
                    <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500">Latest run · {latest.run_number}</p>
                    {latest.results.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs font-mono px-2 py-1 border border-border/40" data-testid={`hos-test-row-${r.test}`}>
                            {r.status === "PASS"
                                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                                : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />}
                            <div className="min-w-0">
                                <p className={`${r.status === "PASS" ? "text-emerald-200" : "text-red-200"}`}>{r.test}</p>
                                <p className="text-[10px] text-gray-500 truncate">{r.output}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-[11px] text-gray-600">No runs yet.</p>
            )}
        </div>
    );
}

function ConsolePane({ commands, log, onRun }) {
    return (
        <div className="flex flex-col h-full" data-testid="hos-console-pane">
            <div className="p-3 border-b border-border/40">
                <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-2">Available commands</p>
                <div className="space-y-1">
                    {commands.map((c) => (
                        <button key={c.command} onClick={() => onRun(c.command)}
                            className="w-full text-left px-2 py-1.5 text-[11px] font-mono text-gray-300 bg-[#0a0a0a] border border-border/40 hover:border-white/30 hover:text-white transition-colors"
                            data-testid={`hos-console-cmd-${c.command.replace(/\s+/g, "-")}`}>
                            <span className="text-gray-500">{">"}</span> {c.command}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 bg-[#070707]">
                <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-2">History</p>
                {log.length === 0 ? (
                    <p className="text-[11px] text-gray-600 font-mono">No commands run yet.</p>
                ) : (
                    <div className="space-y-2 font-mono text-[11px]">
                        {log.map((entry, i) => (
                            <div key={i} className="border-l-2 border-border/60 pl-2" data-testid={`hos-console-entry-${i}`}>
                                <p className="text-gray-400"><span className="text-gray-600">{">"}</span> {entry.command}</p>
                                <p className="text-gray-300 whitespace-pre-wrap">{entry.response}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
